import { forwardRef, useId, useCallback, useState } from 'react';

import { cn } from '../../utils';

export interface MoneyInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'size'
> {
  currency?: 'MXN' | 'USD';
  label?: string;
  hint?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Raw numeric value as string (e.g. "1000.78") — no commas */
  value?: string;
  /** Fires with raw numeric string (no commas) */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const sizeStyles = {
  sm: 'h-10',
  md: 'h-12',
  lg: 'h-14',
};

/**
 * Format raw numeric string with thousand separators.
 *   "1000"    → "1,000"
 *   "1000."   → "1,000."
 *   "1000.78" → "1,000.78"
 */
function formatDisplay(raw: string): string {
  if (!raw) return '';
  const dotIndex = raw.indexOf('.');
  const intPart = dotIndex >= 0 ? raw.slice(0, dotIndex) : raw;
  const decPart = dotIndex >= 0 ? raw.slice(dotIndex) : '';
  if (!intPart && !decPart) return '';
  const formatted = intPart ? Number(intPart).toLocaleString('en-US') : '0';
  return formatted + decPart;
}

/**
 * MoneyInput — Live-masked money input aligned with the Input primitive.
 *
 * Follows the same pattern as @tankef/ui Input:
 * - Floating label, field-base sizing, aria-invalid, aria-describedby
 * - error/hint below the field
 *
 * Layout: $ [1,000.78] MXN — all in one row.
 * Parent receives raw value (no commas) via onChange.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      currency = 'MXN',
      label,
      hint,
      error,
      size = 'md',
      value,
      onChange,
      className,
      disabled,
      id,
      placeholder = '0.00',
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = useState(false);

    const rawValue = String(value || '');
    const currencySymbols: Record<string, string> = { MXN: '$', USD: '$' };
    const symbol = currencySymbols[currency] || '$';

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const stripped = e.target.value.replace(/,/g, '');
        const cleaned = stripped.replace(/[^\d.]/g, '');

        const parts = cleaned.split('.');
        let sanitized = parts[0] || '';
        if (parts.length > 1) {
          sanitized += '.' + parts.slice(1).join('').slice(0, 2);
        } else if (cleaned.endsWith('.')) {
          sanitized += '.';
        }

        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: sanitized },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange?.(syntheticEvent);
      },
      [onChange]
    );

    return (
      <div className="w-full">
        <div className="relative">
          {/* Floating Label — matches Input primitive */}
          {label && (
            <label
              htmlFor={inputId}
              className={cn(
                'absolute left-3 top-0 -translate-y-1/2 pointer-events-none',
                'bg-tkf-surface px-1 text-sm font-medium transition-colors duration-200',
                hasError
                  ? 'text-tkf-fg-error'
                  : isFocused
                    ? 'text-primary-500'
                    : 'text-tkf-fg-muted',
                disabled && 'text-tkf-fg-disabled'
              )}
            >
              {label}
            </label>
          )}

          {/* $ symbol */}
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-primary-500">
            <span className="text-lg font-bold">{symbol}</span>
          </div>

          {/* Input field */}
          <input
            ref={ref}
            id={inputId}
            type="text"
            inputMode="decimal"
            pattern="[0-9,]*\.?[0-9]*"
            autoComplete="off"
            value={formatDisplay(rawValue)}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            className={cn(
              'block w-full rounded-lg border bg-tkf-surface pl-10 pr-16 text-tkf-fg transition-colors duration-200',
              'focus:outline-none focus:ring-1',
              'disabled:cursor-not-allowed disabled:bg-tkf-surface-muted disabled:text-tkf-fg-disabled',
              'placeholder:text-tkf-fg-subtle',
              hasError
                ? 'border-tkf-line-error focus:border-tkf-line-error focus:ring-tkf-line-error'
                : 'border-tkf-line-medium focus:border-primary-500 focus:ring-primary-500',
              sizeStyles[size],
              'text-lg font-normal',
              className
            )}
            {...props}
          />

          {/* Currency badge */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-4 text-tkf-fg-subtle">
            <span className="text-sm font-semibold">{currency}</span>
          </div>
        </div>

        {error && (
          <p id={`${inputId}-error`} className="mt-1 text-sm text-tkf-fg-error">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="mt-1 text-sm text-tkf-fg-subtle">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

MoneyInput.displayName = 'MoneyInput';
